package fr.frux.feature.treedetail.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.HideImage
import androidx.compose.material.icons.outlined.Refresh
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxMinTouchTarget
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_remove
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_retry
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_text_many
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_text_one
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_title_many
import fr.frux.feature.treedetail.resources.treedetail_failed_photos_title_one
import org.jetbrains.compose.resources.stringResource

private val TITLE_ONE = Res.string.treedetail_failed_photos_title_one
private val TITLE_MANY = Res.string.treedetail_failed_photos_title_many
private val TEXT_ONE = Res.string.treedetail_failed_photos_text_one
private val TEXT_MANY = Res.string.treedetail_failed_photos_text_many

/**
 * Encart « Une photo n'est pas partie » (#633) : l'arbre est déjà sur la carte, seules les photos
 * en échec restent à renvoyer ou à abandonner. Le texte parle au pluriel dès [count] > 1.
 */
@Composable
internal fun FailedPhotosNotice(
    count: Int,
    onRemove: () -> Unit,
    onRetry: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val many = count > 1
    Column(
        modifier =
            modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(20.dp))
                .background(MaterialTheme.colorScheme.errorContainer)
                .padding(FruxSpacing.md)
                .semantics { liveRegion = LiveRegionMode.Polite },
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.Top) {
            Box(
                modifier =
                    Modifier
                        .size(56.dp)
                        .clip(RoundedCornerShape(12.dp))
                        .background(MaterialTheme.colorScheme.tertiaryContainer),
                contentAlignment = Alignment.Center,
            ) {
                Icon(
                    imageVector = Icons.Outlined.HideImage,
                    contentDescription = null,
                    modifier = Modifier.size(24.dp),
                    tint = MaterialTheme.colorScheme.onTertiaryContainer,
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = stringResource(if (many) TITLE_MANY else TITLE_ONE),
                    style = MaterialTheme.typography.titleMedium,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
                Text(
                    text = stringResource(if (many) TEXT_MANY else TEXT_ONE),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm, Alignment.End),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                modifier =
                    Modifier
                        .heightIn(min = FruxMinTouchTarget)
                        .clip(RoundedCornerShape(50))
                        .clickable(onClick = onRemove)
                        .padding(horizontal = FruxSpacing.md),
                contentAlignment = Alignment.Center,
            ) {
                Text(
                    text = stringResource(Res.string.treedetail_failed_photos_remove),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onErrorContainer,
                )
            }
            Box(Modifier.clip(RoundedCornerShape(50)).background(MaterialTheme.colorScheme.surface)) {
                FruxButton(
                    text = stringResource(Res.string.treedetail_failed_photos_retry),
                    onClick = onRetry,
                    style = FruxButtonStyle.Text,
                    icon = Icons.Outlined.Refresh,
                )
            }
        }
    }
}
